import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTableContext } from './TableProvider'
import { Button, Dropdown, MenuProps, Spin, notification } from 'antd'
import { DownloadIcon } from 'src/assets/icons'
import { useTranslation } from 'react-i18next'
import { defaultPagination } from '../table.constants'
import { LoadingOutlined } from '@ant-design/icons'

const FILTER_OPTIONS = {
  PDF: 2,
  XLSX: 1,
}

interface IGetExportPayload {
  type: string
  pageSize: number
  pageIndex: number
  columns: string[]
  pageName: string
  searchTerm: string
  searchColumns: string[]
  isDesc: boolean
  sortColumns: string[]
}

const TableDownloadButton: React.FC = () => {
  const { t } = useTranslation()
  const [downloadStatus, setDownloadStatus] = useState<'pending' | 'success' | 'error'>('pending')
  const {
    displayDownloadBtn = true,
    disableTableControl = false,
    chosenColumns,
    downloadDropdownItems,
    tableSearchData = {},
    tableName,
    total,
    pageName,
    handleDownload,
    getHeaderName,
  } = useTableContext()

  const [api, contextHolder] = notification.useNotification()

  const showExporting = useCallback(() => {
    setDownloadStatus('pending')
    api.info({
      message: t('common:exporting'),
      placement: 'topRight',
      duration: 0,
      icon: (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} size='small' style={{ fontSize: '20px' }} />
      ),
      style: {
        backgroundColor: '#F5FBFF',
        borderRadius: '8px',
        padding: '12px 16px',
        border: '1px solid #43BBFF',
        boxShadow: 'none',
        height: '50px',
        width: '250px',
        display: 'flex',
        alignItems: 'stretch',
      },
    })
  }, [api, t])

  const showExportingError = useCallback(
    (error: any) => {
      setDownloadStatus('error')
      api.error({
        message: t('common:download_failed'),
        description: `${JSON.stringify(error)}`,
        placement: 'topRight',
        duration: 0,
      })
    },
    [api, t],
  )
  useEffect(() => {
    if (downloadStatus === 'success' || downloadStatus === 'error') {
      const timer = setTimeout(() => {
        api.destroy()
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [downloadStatus, api])

  const getExportPayload = useCallback(
    (payload: IGetExportPayload) => {
      const { type, pageSize, pageIndex, columns, pageName, searchTerm, searchColumns, isDesc, sortColumns } = payload
      switch (type) {
        case 'pdf':
          return {
            searchTerm,
            searchColumns,
            isDesc,
            sortColumns,
            pageSize: null,
            pageIndex,
            filterOption: FILTER_OPTIONS.PDF,
            export: {
              chosenColumnNameList: columns.reduce((acc: any, column) => {
                acc[column] = getHeaderName?.(column)
                return acc
              }, {}),
              pageName: pageName,
              header: 'Sunwest Bank',
            },
          }
        case 'xlsx':
          return {
            searchTerm,
            searchColumns,
            isDesc,
            sortColumns,
            pageSize: null,
            pageIndex,
            filterOption: FILTER_OPTIONS.XLSX,
            export: {
              chosenColumnNameList: columns.reduce((acc: any, column) => {
                acc[column] = getHeaderName?.(column)
                return acc
              }, {}),
              pageName: pageName,
              header: 'Sunwest Bank',
            },
          }
        default:
          return {
            searchTerm: '',
            searchColumns: [],
            isDesc: false,
            sortColumns: [],
            pageSize,
            pageIndex,
            filterOption: 1,
            export: {
              chosenColumnNameList: columns.reduce((acc: any, column) => {
                acc[column] = getHeaderName?.(column)
                return acc
              }, {}),
              pageName: 'User List',
              header: 'Sunwest Bank',
            },
          }
      }
    },
    [getHeaderName],
  )

  const formatData = useCallback(
    (type: 'pdf' | 'xlsx') => {
      const payload = getExportPayload({
        type: type,
        pageSize: total ?? 0,
        pageIndex: defaultPagination,
        columns: chosenColumns ?? [],
        pageName: tableName ?? pageName ?? '',
        searchTerm: tableSearchData.searchTerm ?? '',
        searchColumns: tableSearchData.searchColumns ?? [],
        isDesc: tableSearchData.isDesc ?? false,
        sortColumns: tableSearchData.sortColumns ?? [],
      })

      return payload
    },
    [
      chosenColumns,
      getExportPayload,
      pageName,
      tableName,
      tableSearchData.isDesc,
      tableSearchData.searchColumns,
      tableSearchData.searchTerm,
      tableSearchData.sortColumns,
      total,
    ],
  )

  // table download states
  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'pdf',
        label: t('OverallFunctionality:table.export.export_pdf_title'),
        onClick: async () => {
          try {
            const payload = formatData('pdf')
            showExporting()
            await handleDownload?.(payload)
          } catch (error) {
            showExportingError(error)
          } finally {
            setDownloadStatus('success')
          }
        },
      },
      {
        key: 'excel',
        label: t('OverallFunctionality:table.export.export_excel_title'),
        onClick: async () => {
          try {
            const payload = formatData('xlsx')
            showExporting()
            await handleDownload?.(payload)
          } catch (error) {
            showExportingError(error)
          } finally {
            setDownloadStatus('success')
          }
        },
      },
    ],
    [formatData, handleDownload, showExporting, showExportingError, t],
  )

  if (!displayDownloadBtn || disableTableControl) {
    return <React.Fragment />
  }

  return (
    <>
      {contextHolder}
      <Dropdown trigger={['click']} menu={{ items: downloadDropdownItems || items }}>
        <Button id='download-button' icon={<DownloadIcon />} />
      </Dropdown>
    </>
  )
}

export default TableDownloadButton
